import React, { FC } from 'react'
import { useTranslation } from 'next-i18next'

import { LockerSizePicking } from 'components/Pages/Locker/LockerBooking/LockerSizePicking'

import type { Space } from 'features/space/types/spaceDetail'

import styles from './SizePickingSection.module.css'

type Props = {
  space: Space
  reservationItems: { [key: string]: number } | null
  onPick: (size: string, count: number) => void
}

export const SizePickingSection: FC<Props> = ({ space, reservationItems, onPick }) => {
  const { t } = useTranslation()
  const usesLockerSizeLabels =
    space.space_type === 'pudo' ||
    space.space_type === 'locker' ||
    space.space_type === 'multiecube'

  const convertMmToCm = (mm: number | null): number | null => {
    if (mm === null) return null
    return Math.round(mm / 10)
  }

  return (
    <>
      <p className={styles.label}>{t('LOCKER.LABEL.NUMBER_OF_LUGGAGE')}</p>
      <div className={styles.sizePickingContainer}>
        {space.prices.map((priceObj, index) => {
          const count = reservationItems?.[priceObj.size] || 0
          const capacity =
            space?.capacity.find((capacity) => capacity.size === priceObj.size)?.quantity || 0
          let description = ''
          let icon = ''
          let label = ''

          const sizeInfo = space?.sizes?.find((s) => s.size === priceObj.size)
          const hasSizeInfo =
            sizeInfo &&
            sizeInfo.width !== null &&
            sizeInfo.length !== null &&
            sizeInfo.height !== null
          if (hasSizeInfo) {
            description = t('LOCKER.DESCRIPTION.SIZE_WITH_DIMENSIONS', {
              width: convertMmToCm(sizeInfo.width),
              length: convertMmToCm(sizeInfo.length),
              height: convertMmToCm(sizeInfo.height),
            })
          }

          switch (priceObj.size) {
            case 'small':
              if (usesLockerSizeLabels) {
                label = t('LOCKER.LABEL.SMALL_SIZE_WITH_PRICE', {
                  price: priceObj.price.toLocaleString(),
                })
                icon = '/locker_small_black.svg'
              } else {
                label = t('COMMON.BAG_SIZE')
                icon = '/bag_icon_black.svg'
              }
              break
            case 'medium':
              label = t('LOCKER.LABEL.MEDIUM_SIZE_WITH_PRICE', {
                price: priceObj.price.toLocaleString(),
              })
              icon = '/locker_medium_black.svg'
              break
            case 'large':
              if (usesLockerSizeLabels) {
                label = t('LOCKER.LABEL.LARGE_SIZE_WITH_PRICE', {
                  price: priceObj.price.toLocaleString(),
                })
                icon = '/locker_large_black.svg'
              } else {
                label = t('COMMON.SUITECASE_SIZE')
                icon = '/luggage_icon.svg'
              }
              break
            default:
              break
          }

          if (!label) return null

          return (
            <div className={styles.sizePickingItem} key={index}>
              <LockerSizePicking
                className={styles.sizePickingRow}
                icon={icon}
                label={label}
                count={count}
                total={capacity}
                onPicked={(count) => onPick(priceObj.size, count)}
              />
              {!!description && <p className={styles.sizePickingDescription}>{description}</p>}
            </div>
          )
        })}
      </div>
    </>
  )
}
